import type { BulkUpdateRepoTagsResult, CreateRepoInput, UpdateRepoInput } from '@mesa/core/repo/mutations';
import { bulkDeleteRepos, bulkUpdateRepoTags, createRepo, deleteRepo, updateRepo } from '@mesa/core/repo/mutations';
import type { ListReposOpts } from '@mesa/core/repo/queries';
import { getRepoByName, listRepos } from '@mesa/core/repo/queries';
import { ZERO_OID_HEX } from '@mesa/core/vcs/zero-oid';
import { createServerFn } from '@tanstack/react-start';
import { log } from '../lib/logger';
import { assertScope, baseAuthMiddleware } from '../middleware/base-auth';
import { vcsAuthMiddleware } from '../middleware/vcs-auth';

// Domain queries load upstream credentials (encryptedDek, encryptedToken) for
// API route responses. Web server functions must strip these before returning
// since TanStack serializes the full object to the browser.
export function stripCredentials<T extends { upstream: { credential?: unknown } | null }>(repo: T): T {
  if (!repo.upstream) return repo;
  const { credential: _, ...upstream } = repo.upstream;
  return { ...repo, upstream } as T;
}

export const getRepo = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string; name: string }) => data)
  .handler(async ({ data: { name }, context: { org, member } }) => {
    assertScope(member.role, 'read');
    const result = await getRepoByName(org.id, name);
    return { ...result, repo: stripCredentials(result.repo) };
  });

export const listReposServerFn = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string } & ListReposOpts) => data)
  .handler(async ({ data: { orgSlug: _orgSlug, ...opts }, context: { org, member } }) => {
    assertScope(member.role, 'read');
    const result = await listRepos(org.id, opts);
    return {
      ...result,
      repos: result.repos.map(stripCredentials),
    };
  });

export const createRepoServerFn = createServerFn({ method: 'POST' })
  .middleware([vcsAuthMiddleware])
  .inputValidator((data: { orgSlug: string } & CreateRepoInput) => data)
  .handler(async ({ data: { orgSlug: _orgSlug, ...input }, context: { org, member, vcs } }) => {
    assertScope(member.role, 'write');
    const { repo } = await createRepo(org.id, input);
    try {
      await vcs.createBookmark(repo.id, repo.defaultBranch, ZERO_OID_HEX);
    } catch (err) {
      log.error('create-repo-bookmark-failed', {
        orgId: org.id,
        repoId: repo.id,
        branch: repo.defaultBranch,
        error: String(err),
      });
      throw err;
    }
    return stripCredentials(repo);
  });

export const updateRepoServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string; repoId: string; currentName: string; patch: UpdateRepoInput }) => data)
  .handler(async ({ data: { repoId, currentName, patch }, context: { org, member } }) => {
    assertScope(member.role, 'write');
    const updated = await updateRepo(org.id, repoId, currentName, patch);
    return stripCredentials(updated);
  });

export const deleteRepoServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string; repoId: string }) => data)
  .handler(async ({ data: { repoId }, context: { org, member } }) => {
    assertScope(member.role, 'write');
    return deleteRepo(org.id, repoId);
  });

export const bulkDeleteReposServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string; repoIds: string[] }) => data)
  .handler(async ({ data: { repoIds }, context: { org, member } }) => {
    assertScope(member.role, 'write');
    await bulkDeleteRepos(org.id, repoIds);
  });

export const bulkUpdateRepoTagsServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .inputValidator(
    (data: { orgSlug: string; repoIds: string[]; action: 'set' | 'remove'; key: string; value?: string }) => data
  )
  .handler(async ({ data: { repoIds, action, key, value }, context: { org, member } }) => {
    assertScope(member.role, 'write');
    return bulkUpdateRepoTags(org.id, repoIds, action, key, value) as Promise<BulkUpdateRepoTagsResult>;
  });
